import gql from 'graphql-tag';

import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {IsDesignMissingRequiredApprovalStudyNodeFragment as IsDesignMissingRequiredApprovalStudyNodeFragmentType} from './__generated/IsDesignMissingRequiredApprovalStudyNodeFragment';

// NOTE: We don't declare a separate query per study type and instead have this shared query fragment as we
// expect `studyDesign` to be used by all future study types. Custom studies is the exception (`designEntry`),
// which we hope to eventually migrate to a document that implements the StudyDesignInterface.
export const IsDesignMissingRequiredApprovalStudyNodeFragment: FragmentWrapper<IsDesignMissingRequiredApprovalStudyNodeFragmentType> =
  {
    fragment: gql`
      fragment IsDesignMissingRequiredApprovalStudyNodeFragment on StudyNode {
        id
        studySchema {
          id
          isDesignApprovalRequired
        }
        designEntry {
          ... on ReviewableInterface {
            id
            review {
              id
              status
            }
          }
        }
        studyDesign {
          ... on ReviewableInterface {
            id
            review {
              id
              status
            }
          }
        }
      }
    `,
    fragmentName: 'IsDesignMissingRequiredApprovalStudyNodeFragment',
  };

/**
 * Returns true if a study's schema requires design approval before proceeding to execution,
 * but the study's design has not been approved yet.
 *
 * Caller should embed {@link IS_DESIGN_MISSING_REQUIRED_APPROVAL_STUDY_NODE_FRAGMENT} in its query
 * for the StudyNode.
 */
export function isDesignMissingRequiredApprovalForStudyExecution(
  study: IsDesignMissingRequiredApprovalStudyNodeFragmentType
): boolean {
  if (!study.studySchema.isDesignApprovalRequired) {
    return false;
  }
  const review =
    study.designEntry && !isInaccessible(study.designEntry)
      ? study.designEntry.review
      : study.studyDesign?.review;

  return review == null || review.status !== ReviewStatus.ACCEPTED;
}
